<template>
  <loading-content :loading="loading">
    <!-- Süsteem -> Seaded -> Uued vaated -> Objekt uues kujunduses: the same
         tabs and the same pages under them, with the address as a heading, its
         standing as pills and how many rows each tab holds. -->
    <div v-if="plate" class="obj-plate">
      <template v-if="!loading">
        <div class="obj-head">
          <div class="obj-head__text">
            <span class="obj-kicker obj-mid">{{ kicker }}</span>
            <div class="obj-title">
              <h1 class="obj-wide">{{ object.address || $address(object.fullAddress) }}</h1>
              <span v-if="place" class="obj-place">{{ place }}</span>
            </div>
            <div class="obj-pills">
              <span v-if="!isContract" class="obj-pill obj-pill--plain">Tavaline</span>
              <!-- Aktiivne is a contract's state; an ordinary object has none. -->
              <span v-if="isContract && object.active" class="obj-pill obj-pill--ok"><i />Aktiivne<template v-if="validDay(object.activeDate)"> alates {{ day(object.activeDate) }}</template></span>
              <span v-else-if="isContract" class="obj-pill obj-pill--plain"><i />Mitteaktiivne</span>
              <router-link v-if="overdue" :to="tabLink('maintenance')" class="obj-pill obj-pill--late"><i />Hooldus hiljaks · {{ overdue }} {{ overdue === 1 ? 'tähtaeg' : 'tähtaega' }}</router-link>
              <span v-if="object.problematic" class="obj-pill obj-pill--late">Probleemne</span>
              <span v-if="isContract && object.key" class="obj-pill obj-pill--plain">Võti kontoris</span>
            </div>
          </div>
          <router-link :to="{ ...tabLink('notes'), query: { lisa: '1' } }" class="obj-btn obj-btn--outline">
            <v-icon size="18">mdi-plus</v-icon>Lisa märkus
          </router-link>
          <router-link v-if="canEdit('object', object)" :to="{ name: 'object/edit', params: { objectId } }" class="obj-btn">
            <v-icon size="18">mdi-pencil-outline</v-icon>Muuda
          </router-link>
          <v-menu location="bottom end">
            <template #activator="{ props }">
              <button type="button" class="obj-icon-btn" aria-label="Rohkem" v-bind="props">
                <v-icon>mdi-dots-vertical</v-icon>
              </button>
            </template>
            <div class="obj-menu">
              <router-link :to="{ name: 'object/notes/print', params: { objectId } }">Prindi ajalugu</router-link>
              <button v-if="canDelete('object', object)" type="button" class="obj-menu__danger" @click="confirmDelete = true">Kustuta objekt</button>
            </div>
          </v-menu>
        </div>
        <!-- Why it is Probleemne, the object's own and its client's. -->
        <div v-if="object.problematic && object.problematicNote" class="obj-reason-band">
          <span class="obj-cap">PROBLEEMNE OBJEKT — PÕHJUS</span>
          <span>{{ object.problematicNote }}</span>
        </div>
        <div v-if="object.client && object.client.problematic && object.client.problematicNote" class="obj-reason-band">
          <span class="obj-cap">PROBLEEMNE KLIENT ({{ object.client.fullName }}) — PÕHJUS</span>
          <span>{{ object.client.problematicNote }}</span>
        </div>
        <nav class="obj-tabs" aria-label="Objekti sakid">
          <router-link v-for="item in menu" :key="item.title" :to="item.link" class="obj-tab">
            {{ item.title }}<span v-if="item.count" class="obj-tab__n" :class="{ 'obj-tab__n--late': item.late }">{{ item.count }}</span>
          </router-link>
        </nav>
      </template>
      <router-view class="mt-4" :object="object" :objectId="objectId" plate :overview="overview" @changed="refreshOverview" />
    </div>
    <template v-else>
    <template v-if="!loading">
      <div class="d-flex flex-row justify-space-between">
        <div class="text-h4">{{$address(object.fullAddress)}}</div>
        <dropdown v-if="menuItems.length" title="Halda" :items="menuItems" />
      </div>
      <v-tabs class="mt-4" bg-color="blue-grey-lighten-5" show-arrows>
        <v-tab v-for="item in menu" :key="item.title" :to="item.link">{{item.title}}</v-tab>
      </v-tabs>
    </template>
    <router-view class="mt-4" :object="object" :objectId="objectId" />
    </template>
    <confirm
      @cancel="confirmDelete = null"
      @confirm="destroy()"
      :visible="!!confirmDelete"
      heading="Kustuta objekt?"
      text="Objekt kaob nimekirjast ja otsingust. Töökäskudega objekti ei saa kustutada."
      action="Kustuta objekt"
      :detail="$address(object.fullAddress)"
      :detail-sub="object.contract ? 'Leping ' + object.contract : ''"
    />
  </loading-content>
</template>

<script>
import api from '@/api'
import LoadingContent from '@/components/LoadingContent.vue'
import helpersMixin from '@/plugins/helpersMixin'
import { mapState, mapActions } from 'vuex'
import dayjs from 'dayjs'
import loadArchivo from '@/plugins/archivo'
import '@/styles/object-plate.css'

export default {
  mixins: [ helpersMixin ],
  components: { LoadingContent },
  data () {
    return {
      loading: false,
      object: {},
      // Counts, hooldus and "Viimati siin" for the new look; null until read.
      overview: null,
      confirmDelete: null
    }
  },
  computed: {
    // Layout.vue asks for these on mount; the tab list is a synchronous
    // computed, so it reads them from the store rather than fetching its own.
    ...mapState(['features']),
    objectId () {
      return this.$route.params.objectId
    },
    plate () {
      return !!(this.features && this.features.objectPlate)
    },
    isContract () {
      return this.object.type === this.objectTypes.CONTRACT
    },
    counts () {
      return (this.overview && this.overview.counts) || {}
    },
    overdue () {
      return this.isContract ? (this.counts.overdue || 0) : 0
    },
    kicker () {
      const parts = ['OBJEKTID', this.isContract ? 'LEPINGULINE' : 'TAVALINE']
      return parts.join(' › ') + (this.isContract && this.object.contract ? ' · LEPING ' + this.object.contract : '')
    },
    // What the heading leaves out of the full address: the city, mostly.
    place () {
      const full = this.$address(this.object.fullAddress) || ''
      const own = (this.object.address || '').trim()
      if (!own || !full.toLowerCase().startsWith(own.toLowerCase())) return ''
      return full.slice(own.length).replace(/^[\s,]+/, '')
    },
    menu () {
      return [{
        title: 'Info',
        link: { name: 'object/view/info', params: { objectId: this.objectId }}
      }, {
        title: 'Seadmed',
        link: { name: 'object/view/devices', params: { objectId: this.objectId }},
        count: this.counts.devices
      }, {
        title: 'Märkused',
        link: { name: 'object/view/notes', params: { objectId: this.objectId }},
        count: this.counts.notes
      }, {
        title: 'Fotod',
        link: { name: 'object/view/photos', params: { objectId: this.objectId }},
        count: this.counts.photos
      }, {
        title: 'Kalender',
        link: { name: 'object/view/calendar', params: { objectId: this.objectId }},
        count: this.counts.events
      }, {
        title: 'Hooldus',
        link: { name: 'object/view/maintenance', params: { objectId: this.objectId }},
        // Deadlines belong to a contract; the new look leaves the tab off an
        // ordinary object, which has none.
        hide: !this.isAdmin || (this.plate && !this.isContract),
        count: this.overdue,
        late: true
      }, {
        title: 'Ajalugu',
        link: { name: 'object/view/history', params: { objectId: this.objectId }},
        count: this.counts.history
      }, {
        // The akt module is deployment-optional (Süsteem -> Seaded); with it off
        // there are no acts and /api/akt answers 404, so the tab goes with it.
        title: 'Aktid',
        link: { name: 'object/view/akts', params: { objectId: this.objectId }},
        hide: !this.features.akt || (this.plate && !this.isContract && !this.counts.akts),
        count: this.counts.akts
      }].filter(({ hide }) => !hide)
    },
    menuItems () {
      return [{
        title: 'Muuda',
        link: { name: 'object/edit', params: { objectId: this.objectId }},
        hide: !this.canEdit('object', this.object)
      }, {
        title: 'Kustuta',
        hide: !this.canDelete('object', this.object),
        onClick: () => {
          this.confirmDelete = true
        }
      }].filter(({ hide }) => !hide)
    }
  },
  watch: {
    async objectId () {
      this.refresh()
    },
    // A tab may have added or removed something its count says.
    '$route.name' () {
      this.refreshOverview()
    },
    plate: {
      handler (on) {
        if (on) {
          loadArchivo()
          this.refreshOverview()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    async refresh () {
      if (!this.objectId) {
        return
      }
      this.loading = true
      const { object } = await api.getObject(this.objectId)
      this.object = object
      this.loading = false
      this.refreshOverview()
    },
    // Only the new look asks; a failure leaves the tabs without numbers rather
    // than the page without content.
    async refreshOverview () {
      if (!this.plate || !this.objectId) return
      const objectId = this.objectId
      try {
        const overview = await api.getObjectOverview(objectId)
        if (objectId === this.objectId) this.overview = overview
      } catch (err) {
        this.overview = null
      }
    },
    tabLink (tab) {
      return { name: 'object/view/' + tab, params: { objectId: this.objectId } }
    },
    day (date) {
      return date ? dayjs(date).format('DD.MM.YYYY') : '—'
    },
    // The server sends "Invalid date" for an object never switched on.
    validDay (date) {
      return !!date && dayjs(date).isValid()
    },
    async destroy () {
      this.loading = true
      this.confirmDelete = null
      try {
        await api.deletObject(this.objectId)
      } catch (err) {
        this.showNotification({ text: err.message || 'Kustutamine ebaõnnestus', color: 'red' })
        this.loading = false
        return
      }
      this.showNotification({ text: 'Objekt kustutatud', color: 'success' })
      // Replace, not push: Back must not return to an object that is gone.
      this.$router.replace({ name: 'object/list', params: { type: 'all' }}).catch(() => {})
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>